/**
 * PodcastsPage - Spotify-like podcast player interface
 * Supports both Audio and Video podcasts
 */
import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Play, Pause, SkipBack, SkipForward, Volume2, VolumeX,
  Clock, Calendar, ChevronDown, ChevronUp, Share2, 
  Headphones, List, Grid, Search, X, Loader2, Video, Youtube
} from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Format duration string (could be "HH:MM:SS" or "MM:SS" or seconds)
const formatDuration = (duration) => {
  if (!duration) return '--:--';
  
  // If it's already formatted
  if (typeof duration === 'string' && duration.includes(':')) {
    return duration;
  }
  
  // If it's seconds
  const totalSeconds = parseInt(duration);
  if (isNaN(totalSeconds)) return duration;
  
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Strip HTML tags from description
const stripHtml = (html) => {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').substring(0, 200) + '...';
};

// Extract YouTube video ID from URL
const extractYoutubeId = (url) => {
  if (!url) return null;
  const patterns = [
    /youtube\.com\/watch\?v=([^&]+)/,
    /youtu\.be\/([^?&]+)/,
    /youtube\.com\/embed\/([^?&]+)/
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

export const PodcastsPage = () => {
  const [activeTab, setActiveTab] = useState('audio'); // 'audio' or 'video'
  const [podcasts, setPodcasts] = useState([]);
  const [videoPodcasts, setVideoPodcasts] = useState([]);
  const [selectedPodcast, setSelectedPodcast] = useState(null);
  const [selectedVideoPodcast, setSelectedVideoPodcast] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [episodesLoading, setEpisodesLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  
  // Video player state
  const [playingVideoEpisode, setPlayingVideoEpisode] = useState(null);
  
  // Audio player state
  const [currentEpisode, setCurrentEpisode] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [showPlayer, setShowPlayer] = useState(false);
  
  const audioRef = useRef(null);

  useEffect(() => {
    fetchPodcasts();
    fetchVideoPodcasts();
  }, []);

  useEffect(() => {
    if (selectedPodcast) {
      fetchEpisodes(selectedPodcast.id);
    }
  }, [selectedPodcast]);

  const fetchPodcasts = async () => {
    try {
      const res = await axios.get(`${API}/podcasts`);
      setPodcasts(res.data || []);
    } catch (err) {
      console.error('Failed to fetch podcasts:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchVideoPodcasts = async () => {
    try {
      const res = await axios.get(`${API}/podcasts/video/list`);
      setVideoPodcasts(res.data || []);
    } catch (err) {
      console.error('Failed to fetch video podcasts:', err);
    }
  };

  const fetchEpisodes = async (podcastId) => {
    setEpisodesLoading(true);
    try {
      const res = await axios.get(`${API}/podcasts/${podcastId}`);
      setEpisodes(res.data.episodes || []);
    } catch (err) {
      console.error('Failed to fetch episodes:', err);
    } finally {
      setEpisodesLoading(false);
    }
  };

  const playEpisode = (episode, podcast) => {
    if (!episode.audio_url) return;
    
    setCurrentEpisode({ ...episode, podcast });
    setShowPlayer(true);
    setIsPlaying(true);
    
    if (audioRef.current) {
      audioRef.current.src = episode.audio_url;
      audioRef.current.play().catch(console.error);
    }
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(console.error);
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const newTime = percent * duration;
    
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const skipForward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.min(audioRef.current.currentTime + 30, duration);
    }
  };

  const skipBackward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(audioRef.current.currentTime - 15, 0);
    }
  };

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  const formatPlayerTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const filteredPodcasts = podcasts.filter(p => 
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.author?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <main className="min-h-screen bg-gradient-to-b from-zinc-900 to-black pb-32">
      {/* Hidden Audio Element */}
      <audio
        ref={audioRef}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={() => setIsPlaying(false)}
        onPause={() => setIsPlaying(false)}
        onPlay={() => setIsPlaying(true)}
      />

      {/* Header */}
      <div className="bg-gradient-to-b from-orange-900/30 to-transparent pt-8 pb-12 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <Headphones className="w-10 h-10 text-orange-500" />
            <h1 className="font-heading text-4xl md:text-5xl font-bold uppercase tracking-tight text-white">
              Podcasts
            </h1>
          </div>
          
          {/* Tab Switcher */}
          <div className="flex items-center gap-4 mb-6">
            <button
              onClick={() => { setActiveTab('audio'); setSelectedPodcast(null); setPlayingVideoEpisode(null); }}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full transition-all ${
                activeTab === 'audio'
                  ? 'bg-orange-500 text-white font-bold'
                  : 'bg-zinc-800/50 text-zinc-400 hover:text-white hover:bg-zinc-800'
              }`}
              data-testid="audio-tab-btn"
            >
              <Headphones className="w-4 h-4" />
              <span>Audio</span>
              {podcasts.length > 0 && <span className="bg-black/20 px-2 py-0.5 rounded-full text-xs">{podcasts.length}</span>}
            </button>
            <button
              onClick={() => { setActiveTab('video'); setSelectedVideoPodcast(null); setPlayingVideoEpisode(null); }}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full transition-all ${
                activeTab === 'video'
                  ? 'bg-red-600 text-white font-bold'
                  : 'bg-zinc-800/50 text-zinc-400 hover:text-white hover:bg-zinc-800'
              }`}
              data-testid="video-tab-btn"
            >
              <Video className="w-4 h-4" />
              <span>Video</span>
              {videoPodcasts.length > 0 && <span className="bg-black/20 px-2 py-0.5 rounded-full text-xs">{videoPodcasts.length}</span>}
            </button>
          </div>
          
          {/* Search and View Toggle */}
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={`Search ${activeTab} podcasts...`}
                className="w-full pl-10 pr-4 py-2 bg-zinc-800/50 border border-white/10 text-white placeholder-zinc-500 focus:outline-none focus:border-orange-500"
                data-testid="podcast-search"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 ${viewMode === 'grid' ? 'text-white bg-white/10' : 'text-zinc-500'}`}
              >
                <Grid className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 ${viewMode === 'list' ? 'text-white bg-white/10' : 'text-zinc-500'}`}
              >
                <List className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
          </div>
        ) : activeTab === 'audio' ? (
          /* AUDIO PODCASTS TAB */
          podcasts.length === 0 ? (
            <div className="text-center py-20">
              <Headphones className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
              <h2 className="text-xl text-white font-bold mb-2">No Audio Podcasts Yet</h2>
              <p className="text-zinc-500">Check back soon for sports podcasts and audio content.</p>
            </div>
          ) : (
          <div className="flex gap-8">
            {/* Podcast List */}
            <div className={`${selectedPodcast ? 'hidden md:block md:w-1/3' : 'w-full'}`}>
              <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-4">
                All Shows ({filteredPodcasts.length})
              </h2>
              
              <div className={viewMode === 'grid' ? 'grid grid-cols-2 lg:grid-cols-3 gap-4' : 'space-y-2'}>
                {filteredPodcasts.map((podcast) => (
                  <motion.div
                    key={podcast.id}
                    whileHover={{ scale: 1.02 }}
                    onClick={() => setSelectedPodcast(podcast)}
                    className={`cursor-pointer group ${
                      viewMode === 'grid' 
                        ? 'bg-zinc-800/30 hover:bg-zinc-800/60 p-4 transition-colors'
                        : 'flex items-center gap-4 p-3 hover:bg-zinc-800/40 transition-colors'
                    } ${selectedPodcast?.id === podcast.id ? 'ring-2 ring-orange-500' : ''}`}
                    data-testid={`podcast-${podcast.id}`}
                  >
                    {/* Podcast Art */}
                    <div className={`${viewMode === 'grid' ? 'aspect-square mb-3' : 'w-16 h-16 flex-shrink-0'} bg-zinc-700 overflow-hidden shadow-lg`}>
                      {podcast.image_url ? (
                        <img 
                          src={podcast.image_url} 
                          alt={podcast.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Headphones className="w-8 h-8 text-zinc-600" />
                        </div>
                      )}
                    </div>
                    
                    <div className={viewMode === 'grid' ? '' : 'flex-1 min-w-0'}>
                      <h3 className="text-white font-bold text-sm truncate group-hover:text-orange-500 transition-colors">
                        {podcast.name}
                      </h3>
                      <p className="text-zinc-500 text-xs truncate">{podcast.author}</p>
                      <p className="text-zinc-600 text-xs mt-1">{podcast.episode_count} episodes</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Episode List */}
            {selectedPodcast && (
              <div className="flex-1">
                {/* Back Button (Mobile) */}
                <button
                  onClick={() => setSelectedPodcast(null)}
                  className="md:hidden flex items-center gap-2 text-zinc-400 hover:text-white mb-4"
                >
                  <ChevronDown className="w-4 h-4 rotate-90" />
                  Back to all podcasts
                </button>

                {/* Podcast Header */}
                <div className="flex items-start gap-6 mb-8">
                  <div className="w-48 h-48 bg-zinc-800 shadow-2xl flex-shrink-0 overflow-hidden">
                    {selectedPodcast.image_url ? (
                      <img 
                        src={selectedPodcast.image_url} 
                        alt={selectedPodcast.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Headphones className="w-16 h-16 text-zinc-600" />
                      </div>
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold uppercase tracking-widest text-orange-500 mb-2">Podcast</p>
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">{selectedPodcast.name}</h2>
                    <p className="text-zinc-400 mb-4">{selectedPodcast.author}</p>
                    <p className="text-zinc-500 text-sm line-clamp-2">{selectedPodcast.description}</p>
                  </div>
                </div>

                {/* Episodes */}
                <div>
                  <h3 className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-4">
                    Episodes
                  </h3>
                  
                  {episodesLoading ? (
                    <div className="flex items-center justify-center py-10">
                      <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {episodes.map((episode, index) => (
                        <motion.div
                          key={episode.id || index}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className={`p-4 hover:bg-zinc-800/40 transition-colors group ${
                            currentEpisode?.id === episode.id ? 'bg-zinc-800/60' : ''
                          }`}
                          data-testid={`episode-${episode.id}`}
                        >
                          <div className="flex items-start gap-4">
                            {/* Play Button */}
                            <button
                              onClick={() => playEpisode(episode, selectedPodcast)}
                              disabled={!episode.audio_url}
                              className="w-12 h-12 bg-orange-500 hover:bg-orange-400 disabled:bg-zinc-700 disabled:cursor-not-allowed rounded-full flex items-center justify-center flex-shrink-0 transition-colors shadow-lg"
                              data-testid={`play-episode-${episode.id}`}
                            >
                              {currentEpisode?.id === episode.id && isPlaying ? (
                                <Pause className="w-5 h-5 text-black" />
                              ) : (
                                <Play className="w-5 h-5 text-black ml-0.5" />
                              )}
                            </button>
                            
                            {/* Episode Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 text-xs text-zinc-500 mb-1">
                                {episode.published_date && (
                                  <span>{formatDate(episode.published_date)}</span>
                                )}
                                {episode.duration && (
                                  <>
                                    <span>•</span>
                                    <span className="flex items-center gap-1">
                                      <Clock className="w-3 h-3" />
                                      {formatDuration(episode.duration)}
                                    </span>
                                  </>
                                )}
                              </div>
                              <h4 className="text-white font-medium mb-1 group-hover:text-orange-500 transition-colors">
                                {episode.title}
                              </h4>
                              <p className="text-zinc-500 text-sm line-clamp-2">
                                {stripHtml(episode.description)}
                              </p>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          )
        ) : (
          /* VIDEO PODCASTS TAB */
          videoPodcasts.length === 0 ? (
            <div className="text-center py-20">
              <Video className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
              <h2 className="text-xl text-white font-bold mb-2">No Video Podcasts Yet</h2>
              <p className="text-zinc-500">Check back soon for video content from YouTube channels and playlists.</p>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Video Player (if playing) */}
              {playingVideoEpisode && (
                <div className="bg-zinc-900 rounded-xl overflow-hidden">
                  <div className="aspect-video w-full">
                    <iframe
                      src={`https://www.youtube.com/embed/${playingVideoEpisode.youtube_id}?autoplay=1`}
                      title={playingVideoEpisode.title}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="text-white font-bold text-lg">{playingVideoEpisode.title}</h3>
                    <p className="text-zinc-400 text-sm mt-1">{playingVideoEpisode.description}</p>
                    <button
                      onClick={() => setPlayingVideoEpisode(null)}
                      className="mt-3 text-zinc-500 hover:text-white text-sm flex items-center gap-1"
                    >
                      <X className="w-4 h-4" /> Close Player
                    </button>
                  </div>
                </div>
              )}

              {/* Video Podcasts Grid */}
              <div className="flex gap-8">
                {/* Podcast List */}
                <div className={`${selectedVideoPodcast ? 'hidden md:block md:w-1/3' : 'w-full'}`}>
                  <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-4">
                    Video Shows ({videoPodcasts.filter(p => 
                      !searchQuery || p.name?.toLowerCase().includes(searchQuery.toLowerCase())
                    ).length})
                  </h2>
                  
                  <div className={viewMode === 'grid' ? 'grid grid-cols-2 lg:grid-cols-3 gap-4' : 'space-y-2'}>
                    {videoPodcasts
                      .filter(p => !searchQuery || p.name?.toLowerCase().includes(searchQuery.toLowerCase()))
                      .map((podcast) => (
                      <motion.div
                        key={podcast.id}
                        whileHover={{ scale: 1.02 }}
                        onClick={() => setSelectedVideoPodcast(podcast)}
                        className={`cursor-pointer group ${
                          viewMode === 'grid' 
                            ? 'bg-zinc-800/30 hover:bg-zinc-800/60 p-4 transition-colors'
                            : 'flex items-center gap-4 p-3 hover:bg-zinc-800/40 transition-colors'
                        } ${selectedVideoPodcast?.id === podcast.id ? 'ring-2 ring-red-500' : ''}`}
                        data-testid={`video-podcast-${podcast.id}`}
                      >
                        <div className={`${viewMode === 'grid' ? 'aspect-video mb-3' : 'w-20 h-12 flex-shrink-0'} bg-zinc-700 overflow-hidden shadow-lg relative`}>
                          {podcast.image_url ? (
                            <img 
                              src={podcast.image_url} 
                              alt={podcast.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Youtube className="w-8 h-8 text-red-500" />
                            </div>
                          )}
                          <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <Play className="w-10 h-10 text-white" />
                          </div>
                        </div>
                        
                        <div className={viewMode === 'grid' ? '' : 'flex-1 min-w-0'}>
                          <h3 className="text-white font-semibold text-sm truncate group-hover:text-red-400 transition-colors">
                            {podcast.name}
                          </h3>
                          <p className="text-zinc-500 text-xs mt-1">
                            {podcast.episode_count || 0} episodes
                          </p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Selected Video Podcast Detail */}
                {selectedVideoPodcast && (
                  <div className="flex-1 md:w-2/3">
                    <div className="sticky top-4">
                      {/* Back Button (mobile) */}
                      <button
                        onClick={() => setSelectedVideoPodcast(null)}
                        className="md:hidden flex items-center gap-2 text-zinc-400 hover:text-white mb-4"
                      >
                        <ChevronUp className="w-5 h-5 rotate-[-90deg]" />
                        Back to Shows
                      </button>

                      {/* Podcast Header */}
                      <div className="flex gap-6 mb-6">
                        <div className="w-32 h-32 flex-shrink-0 bg-zinc-800 shadow-xl overflow-hidden">
                          {selectedVideoPodcast.image_url ? (
                            <img 
                              src={selectedVideoPodcast.image_url} 
                              alt={selectedVideoPodcast.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Youtube className="w-16 h-16 text-red-500" />
                            </div>
                          )}
                        </div>
                        
                        <div className="flex-1">
                          <span className="text-xs font-bold uppercase tracking-widest text-red-500">
                            Video Podcast
                          </span>
                          <h2 className="text-2xl md:text-3xl font-bold text-white mt-1">
                            {selectedVideoPodcast.name}
                          </h2>
                          {selectedVideoPodcast.description && (
                            <p className="text-zinc-400 text-sm mt-2 line-clamp-2">
                              {selectedVideoPodcast.description}
                            </p>
                          )}
                          <div className="flex items-center gap-4 mt-3 text-sm text-zinc-500">
                            <span className="flex items-center gap-1">
                              <Play className="w-4 h-4" />
                              {selectedVideoPodcast.episode_count || 0} episodes
                            </span>
                            <span className="bg-zinc-800 px-2 py-0.5 rounded text-xs">{selectedVideoPodcast.category}</span>
                          </div>
                        </div>
                      </div>

                      {/* Episodes List */}
                      <div>
                        <h3 className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-4">
                          All Episodes
                        </h3>
                        
                        {selectedVideoPodcast.episodes?.length === 0 ? (
                          <div className="text-center py-8 text-zinc-500">
                            <Video className="w-12 h-12 mx-auto mb-2 opacity-50" />
                            <p>No episodes yet</p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {selectedVideoPodcast.episodes?.map((episode) => (
                              <motion.div
                                key={episode.id}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                onClick={() => setPlayingVideoEpisode(episode)}
                                className="flex items-center gap-4 p-3 hover:bg-zinc-800/40 cursor-pointer group transition-colors"
                              >
                                <div className="w-28 h-16 flex-shrink-0 bg-zinc-800 overflow-hidden relative">
                                  <img 
                                    src={episode.thumbnail_url || `https://i.ytimg.com/vi/${episode.youtube_id}/mqdefault.jpg`} 
                                    alt={episode.title}
                                    className="w-full h-full object-cover"
                                  />
                                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Play className="w-8 h-8 text-white" />
                                  </div>
                                </div>
                                
                                <div className="flex-1 min-w-0">
                                  <h4 className="text-white font-medium truncate group-hover:text-red-400 transition-colors">
                                    {episode.title}
                                  </h4>
                                  {episode.description && (
                                    <p className="text-zinc-500 text-sm truncate mt-0.5">
                                      {episode.description}
                                    </p>
                                  )}
                                  <p className="text-zinc-600 text-xs mt-1">
                                    {episode.published_date ? formatDate(episode.published_date) : 'No date'}
                                  </p>
                                </div>
                              </motion.div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )
        )}
      </div>

      {/* Fixed Bottom Player */}
      <AnimatePresence>
        {showPlayer && currentEpisode && (
          <motion.div
            initial={{ y: 100 }}
            animate={{ y: 0 }}
            exit={{ y: 100 }}
            className="fixed bottom-0 left-0 right-0 bg-zinc-900 border-t border-white/10 z-50"
            style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
          >
            {/* Progress Bar */}
            <div 
              className="h-1 bg-zinc-800 cursor-pointer"
              onClick={handleSeek}
            >
              <div 
                className="h-full bg-orange-500"
                style={{ width: `${(currentTime / duration) * 100 || 0}%` }}
              />
            </div>
            
            <div className="px-4 py-3">
              <div className="max-w-7xl mx-auto flex items-center gap-4">
                {/* Episode Info */}
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-12 h-12 bg-zinc-800 flex-shrink-0 overflow-hidden">
                    {currentEpisode.image_url || currentEpisode.podcast?.image_url ? (
                      <img 
                        src={currentEpisode.image_url || currentEpisode.podcast?.image_url} 
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Headphones className="w-5 h-5 text-zinc-600" />
                      </div>
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-white text-sm font-medium truncate">{currentEpisode.title}</p>
                    <p className="text-zinc-500 text-xs truncate">{currentEpisode.podcast?.name}</p>
                  </div>
                </div>
                
                {/* Controls */}
                <div className="flex items-center gap-2">
                  <button 
                    onClick={skipBackward}
                    className="p-2 text-zinc-400 hover:text-white hidden sm:block"
                    title="Skip back 15 seconds"
                  >
                    <SkipBack className="w-5 h-5" />
                  </button>
                  
                  <button
                    onClick={togglePlay}
                    className="w-10 h-10 bg-white rounded-full flex items-center justify-center hover:scale-105 transition-transform"
                  >
                    {isPlaying ? (
                      <Pause className="w-5 h-5 text-black" />
                    ) : (
                      <Play className="w-5 h-5 text-black ml-0.5" />
                    )}
                  </button>
                  
                  <button 
                    onClick={skipForward}
                    className="p-2 text-zinc-400 hover:text-white hidden sm:block"
                    title="Skip forward 30 seconds"
                  >
                    <SkipForward className="w-5 h-5" />
                  </button>
                </div>
                
                {/* Time & Volume */}
                <div className="hidden md:flex items-center gap-4">
                  <span className="text-xs text-zinc-500 w-20 text-right">
                    {formatPlayerTime(currentTime)} / {formatPlayerTime(duration)}
                  </span>
                  
                  <div className="flex items-center gap-2">
                    <button onClick={toggleMute} className="text-zinc-400 hover:text-white">
                      {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                    </button>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      value={isMuted ? 0 : volume}
                      onChange={handleVolumeChange}
                      className="w-20 accent-orange-500"
                    />
                  </div>
                </div>
                
                {/* Close Button */}
                <button
                  onClick={() => {
                    setShowPlayer(false);
                    if (audioRef.current) {
                      audioRef.current.pause();
                    }
                  }}
                  className="p-2 text-zinc-500 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="noise-overlay" aria-hidden="true" />
    </main>
  );
};

export default PodcastsPage;
